import React, { useEffect, useState, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, Title, Tooltip, Legend, PointElement } from 'chart.js';
import ApiServices from '../config/ApiServices';
import { toast } from "react-toastify";
import { useDispatch } from 'react-redux';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { hideLoader } from '../redux/actions/LoaderActions';

ChartJS.register(LineElement, CategoryScale, LinearScale, Title, Tooltip, Legend, PointElement);

const StudentPerformanceOverTime = ({ studentId }) => {
    const dispatch = useDispatch();
    const chartRef = useRef(null);  // Use ref to keep track of the chart instance
    const [studentMarks, setStudentMarks] = useState([]);
    const [filteredMarks, setFilteredMarks] = useState([]);
    const [dateFilter, setDateFilter] = useState('all');

    useEffect(() => {
        if (studentId) {
            fetchStudentPerformance();
        }

        return () => {
            // Cleanup function to destroy the previous chart instance
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [studentId]);

    useEffect(() => {
        filterMarks();
    }, [studentMarks, dateFilter]);
    const fetchStudentPerformance=()=>{
        ApiServices.apiRequest("studentPerformanceWithTime",{studentId:studentId})
        .then((res) => {
            if (res.data.success) {
                setStudentMarks(res.data.data);
            } else {
                //toast.error(res.data.message);
            }
        })
        .catch((err) => {
            //toast.error(err?.message ? err?.message : "Something went wrong");
        })
        .finally(() => {
            dispatch(hideLoader());
        });
    }
    // const fetchStudentPerformance = async () => {
    //     try {
    //         const batchRes = await ApiServices.apiRequest("getAssignStudent", { "students.studentId": studentId, "students.studentstatus": true });
    //         if (batchRes.data.success) {
    //             const batchData = batchRes.data.data;
    //             const taskPromises = batchData.map(batch =>
    //                 ApiServices.apiRequest("getTask", { batchId: batch?.batchId?._id }).then(taskRes => ({
    //                     batchName: batch?.batchId?.name,
    //                     tasks: taskRes.data.data
    //                 }))
    //             );
    //             const taskDataArray = await Promise.all(taskPromises);
    //             const markPromises = taskDataArray.flatMap(taskData =>
    //                 taskData.tasks.map(task =>
    //                     ApiServices.apiRequest("getMarks", { testId: task._id, studentId }).then(markRes => ({
    //                         ...task,
    //                         batchName: taskData.batchName,
    //                         marks: markRes.data.data
    //                     }))
    //                 )
    //             );
    //             const taskMarksArray = await Promise.all(markPromises);
    //             const performanceData = taskMarksArray.flatMap(taskMarks =>
    //                 taskMarks.marks.map(mark => ({
    //                     batchName: taskMarks.batchName,
    //                     taskName: taskMarks.name,
    //                     score: (mark.score / taskMarks.maximumMarks) * 100, // Normalize as percentage
    //                     maximumMarks: taskMarks.maximumMarks,
    //                     date: taskMarks.date // assuming `date` field is present
    //                 }))
    //             );
    //             setStudentMarks(performanceData);
    //         } else {
    //             toast.error(batchRes.data.message);
    //         }
    //     } catch (error) {
    //         toast.error(error.message || "Something went wrong while fetching data");
    //     }
    // };

    const filterMarks = () => {
        const now = moment().startOf('day');
        let filtered = studentMarks;
        switch (dateFilter) {
            case 'today':
                filtered = studentMarks.filter(mark => moment(mark.date, 'YYYY-MM-DD').isSame(now, 'day'));
                break;
            case 'thisWeek':
                filtered = studentMarks.filter(mark => moment(mark.date, 'YYYY-MM-DD').isAfter(moment(now).subtract(7, 'days')));
                break;
            case 'lastMonth':
                filtered = studentMarks.filter(mark => moment(mark.date, 'YYYY-MM-DD').isAfter(moment(now).subtract(1, 'months')));
                break;
            case 'lastThreeMonths':
                filtered = studentMarks.filter(mark => moment(mark.date, 'YYYY-MM-DD').isAfter(moment(now).subtract(3, 'months')));
                break;
            default:
                filtered = studentMarks;
                break;
        }
        setFilteredMarks(filtered);
    };
    

    const lineData = {
        labels: filteredMarks.map(mark => moment(mark.date).format('YYYY-MM-DD')),
        datasets: [
            {
                label: 'Performance Over Time',
                data: filteredMarks.map(mark =>  ((mark.score/mark.maximumMarks*100).toFixed(2))), // Data as percentage
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                pointBackgroundColor: 'rgba(75, 192, 192, 1)',
                fill: true,
                tension: 0.3 // Smooth the line a bit
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            tooltip: {
                callbacks: {
                    title: function (context) {
                        const mark = filteredMarks[context[0].dataIndex];
                        return `${mark.taskName} (${mark.batchName})`;
                    },
                    label: function (context) {
                        const mark = filteredMarks[context.dataIndex];
                        const percentage = ((mark.score/mark.maximumMarks)*100).toFixed(2);
                        return `Marks: ${mark.score}/${mark.maximumMarks} (${percentage}%)`;
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    callback: function(value, index, ticks) {
                        return filteredMarks[index] ? moment(filteredMarks[index].date).format('YYYY-MM-DD') : '';
                    },
                    maxRotation: 45,
                    minRotation: 45,
                }
            },
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'Percentage (%)'
                }
            }
        }
    };

    return (
        <div>
            <div>
                <h2>Student's Performance Over Time</h2>
                <div>
                    <select className='form-control w-25' onChange={(e) => setDateFilter(e.target.value)} value={dateFilter}>
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="thisWeek">This Week</option>
                        <option value="lastMonth">Last Month</option>
                        <option value="lastThreeMonths">Last Three Months</option>
                    </select>
                </div>
                <Line ref={chartRef} data={lineData} options={options} />
            </div>
        </div>
    );
};

export default StudentPerformanceOverTime;
